import { Stack } from "@altimateai/lego";
import { Alert, AlertDescription } from "@altimateai/lego";
import { IconFolder, IconAlertCircle } from "@tabler/icons-react";
import { TableauProject } from "../types";

interface ProjectInfoDisplayProps {
  type: "default" | "selected";
  defaultProjectName?: string;
  selectedProject?: TableauProject;
  hasAltimateWorkbooks?: boolean;
  subprojectName?: string;
  /** True when the connecting PAT role can publish workbooks. When false
   * (Site Administrator Explorer), the picker is in "locate" mode — the
   * subproject existence requirement inverts. */
  canPublishWorkbooks?: boolean;
}

export const ProjectInfoDisplay = ({
  type,
  defaultProjectName,
  selectedProject,
  hasAltimateWorkbooks,
  subprojectName,
  canPublishWorkbooks = true,
}: ProjectInfoDisplayProps) => {
  if (type === "default" && defaultProjectName) {
    return (
      <div className="al-p-4 al-bg-green-50 al-rounded-md al-border al-border-green-200">
        <Stack direction="row" gap="sm" align="center">
          <IconFolder className="al-w-5 al-h-5 al-text-green-600" />
          <div className="al-flex-1">
            <div className="al-font-medium al-text-sm al-text-gray-800">
              Using default location: /Default/{defaultProjectName}
            </div>
            <div className="al-text-xs al-text-gray-600 al-mt-1">
              {canPublishWorkbooks ? (
                <>
                  Workbooks will be created in the &#34;{defaultProjectName}&#34; subproject under
                  Tableau&#39;s Default project
                </>
              ) : (
                <>
                  We&#39;ll locate the existing &#34;{defaultProjectName}&#34; subproject under
                  Tableau&#39;s Default project
                </>
              )}
            </div>
          </div>
        </Stack>
      </div>
    );
  }

  if (type === "selected" && selectedProject && subprojectName) {
    // Block only the Creator clash case: PAT can publish AND the subproject
    // already exists, which would cause a publish clash. Explorer is never
    // blocked in the UI — backend pre-flight is authoritative for "are the
    // workbooks reachable", and if not, Finish surfaces the error.
    if (canPublishWorkbooks && hasAltimateWorkbooks) {
      return (
        <Alert className="al-bg-red-50 al-border-red-200">
          <IconAlertCircle className="al-h-4 al-w-4 al-text-red-600" />
          <AlertDescription className="al-text-sm al-text-gray-700">
            <div className="al-font-semibold al-text-red-800">Cannot use this project</div>
            <div className="al-mt-1">
              The project <span className="al-font-medium">{selectedProject.name}</span> already
              contains a subproject named &#34;{subprojectName}&#34;. Please select a different
              project.
            </div>
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="al-p-3 al-bg-blue-50 al-rounded-md al-border al-border-blue-200">
        <Stack direction="row" gap="sm" align="center">
          <IconFolder className="al-w-5 al-h-5 al-text-blue-600" />
          <div className="al-flex-1">
            <div className="al-font-medium al-text-sm">{selectedProject.name}</div>
            {selectedProject.path && (
              <div className="al-text-xs al-text-gray-600">{selectedProject.path}</div>
            )}
            <div className="al-text-xs al-text-blue-600 al-mt-1">
              {canPublishWorkbooks ? (
                <>
                  → Will create: {selectedProject.path}/{subprojectName}
                </>
              ) : (
                <>
                  → Will look for Altimate workbooks inside{" "}
                  <span className="al-font-medium">{selectedProject.path}</span> (or in a direct
                  child project)
                </>
              )}
            </div>
          </div>
        </Stack>
      </div>
    );
  }

  return null;
};
